import type { LayerData } from '@/atoms/layers.atom';

export type LayerTreeItem = LayerData & { subLayers?: LayerTreeItem[] };
export type FlatLayer = { layer: LayerTreeItem; depth: number; parentId: string | null };
export type DropPosition = 'before' | 'after' | 'inside';
export const ROOT_DROP_ID = 'layers-root-drop';

export function flattenLayerTree(layers: LayerTreeItem[], depth = 0, parentId: string | null = null): FlatLayer[] {
  return layers.flatMap((layer) => [
    { layer, depth, parentId },
    ...flattenLayerTree(layer.subLayers ?? [], depth + 1, layer.id),
  ]);
}

export function canMoveLayer(items: FlatLayer[], layerId: string, parentId: string | null): boolean {
  const byId = new Map(items.map((item) => [item.layer.id, item]));
  const source = byId.get(layerId);
  const parent = parentId ? byId.get(parentId) : undefined;
  if (!source || source.layer.isEphemeral || (parentId && (!parent || parent.layer.isEphemeral))) return false;
  // Existing non-folder hierarchies can be reordered, but only folders accept new children.
  if (parent && parent.layer.type !== 'GROUP' && source.parentId !== parentId) return false;
  let ancestor = parent;
  while (ancestor) {
    if (ancestor.layer.id === layerId) return false;
    ancestor = ancestor.parentId ? byId.get(ancestor.parentId) : undefined;
  }
  const start = items.indexOf(source);
  let subtreeHeight = 0;
  for (let i = start + 1; i < items.length && items[i].depth > source.depth; i++) {
    subtreeHeight = Math.max(subtreeHeight, items[i].depth - source.depth);
  }
  return (parent ? parent.depth + 1 : 0) + subtreeHeight < 32;
}

/** Build a complete sibling order, including roots hidden by type grouping. */
export function projectLayerDrop(
  items: FlatLayer[],
  layerId: string,
  targetId: string,
  position: DropPosition,
  groupByType = false,
  outdent = 0,
) {
  const source = items.find((item) => item.layer.id === layerId);
  let target = items.find((item) => item.layer.id === targetId);
  if (!source || (targetId !== ROOT_DROP_ID && !target) || targetId === layerId) return null;
  while (target && outdent > 0 && target.parentId) {
    target = items.find((item) => item.layer.id === target!.parentId);
    outdent--;
  }
  const parentId = targetId === ROOT_DROP_ID ? null : position === 'inside' ? target!.layer.id : target!.parentId;
  if (!canMoveLayer(items, layerId, parentId)) return null;
  // Reordering across visual type sections would give no visible feedback.
  if (groupByType && parentId === null && target && source.layer.type !== target.layer.type) return null;
  const siblings = items.filter((item) => item.parentId === parentId).map((item) => item.layer.id);
  const orderedSiblingIds = siblings.filter((id) => id !== layerId);
  const targetIndex = target ? orderedSiblingIds.indexOf(target.layer.id) : -1;
  const index = position === 'inside' || targetId === ROOT_DROP_ID
    ? orderedSiblingIds.length
    : targetIndex + (position === 'after' ? 1 : 0);
  orderedSiblingIds.splice(index, 0, layerId);
  return { parentId, orderedSiblingIds, targetId: target?.layer.id ?? ROOT_DROP_ID, position };
}
